import { Fragment, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { formatIQD, formatDate, formatDateTime, formatNumber } from '../../../shared/format.js';
import { PrintLogo } from '../../../shared/PrintLogo.jsx';
import { ExportButton } from '../../reports/components/ExportButton.jsx';
import { useSupplierReport } from '../hooks/useSupplierAccounts.js';
import './print.css';

const REPORTS = [
  { key: 'balances', label: 'أرصدة الموردين' },
  { key: 'debts', label: 'ديون الموردين' },
  { key: 'payments', label: 'تسديدات الموردين' },
  { key: 'purchases', label: 'مشتريات الموردين' },
  { key: 'aging', label: 'أعمار ديون الموردين' },
  { key: 'review', label: 'بحاجة مراجعة' }
];

// تعريف كل تقرير: أعمدته، وكيف يُستخرج كل حقل من الصف، وسطر المجاميع.
function defsFor(kind) {
  if (kind === 'balances') {
    return {
      title: 'تقرير أرصدة الموردين',
      columns: [
        { key: 'supplierName', label: 'اسم المورد' },
        { key: 'phone', label: 'الهاتف' },
        { key: 'purchasesTotal', label: 'إجمالي المشتريات', money: true },
        { key: 'paidTotal', label: 'إجمالي المدفوع', money: true },
        { key: 'remainingTotal', label: 'إجمالي المتبقي', money: true, danger: true },
        { key: 'openInvoices', label: 'عدد الفواتير المستحقة' },
        { key: 'accountStateLabel', label: 'حالة الحساب', pill: true }
      ],
      totalsRow: (t) => ['المجموع', '', t.purchasesTotal, t.paidTotal, t.remainingTotal, t.openInvoices, '']
    };
  }
  if (kind === 'debts') {
    return {
      title: 'تقرير ديون الموردين',
      columns: [
        { key: 'supplierName', label: 'المورد' },
        { key: 'invoiceNumber', label: 'رقم الفاتورة', mono: true },
        { key: 'date', label: 'تاريخ الفاتورة', date: true },
        { key: 'total', label: 'قيمة الفاتورة', money: true },
        { key: 'paid', label: 'المدفوع', money: true },
        { key: 'remaining', label: 'المتبقي', money: true, danger: true },
        { key: 'paymentStatusLabel', label: 'حالة السداد', pill: true },
        { key: 'dueDate', label: 'تاريخ الاستحقاق', date: true }
      ],
      totalsRow: (t) => ['المجموع', '', '', t.total, t.paid, t.remaining, '', '']
    };
  }
  if (kind === 'payments') {
    return {
      title: 'تقرير تسديدات الموردين',
      columns: [
        { key: 'supplierName', label: 'المورد' },
        { key: 'paidAt', label: 'تاريخ التسديد', dateTime: true },
        { key: 'voucherNumber', label: 'رقم سند الصرف', mono: true },
        { key: 'directionLabel', label: 'النوع' },
        { key: 'amount', label: 'المبلغ', money: true },
        { key: 'paymentMethodLabel', label: 'طريقة الدفع' },
        { key: 'cashboxName', label: 'القاصة' },
        { key: 'userName', label: 'المستخدم' },
        { key: 'invoiceNumber', label: 'الفاتورة المرتبطة', mono: true },
        { key: 'statusLabel', label: 'الحالة', pill: true }
      ],
      totalsRow: (t) => ['المجموع', '', '', '', t.paidTotal, '', '', '', '', '']
    };
  }
  if (kind === 'purchases') {
    return {
      title: 'تقرير مشتريات الموردين',
      columns: [
        { key: 'supplierName', label: 'المورد' },
        { key: 'invoicesCount', label: 'عدد الفواتير' },
        { key: 'purchasesTotal', label: 'إجمالي المشتريات', money: true },
        { key: 'productsCount', label: 'عدد المنتجات' },
        { key: 'qtyTotal', label: 'إجمالي الكميات' },
        { key: 'paidTotal', label: 'إجمالي المدفوع', money: true },
        { key: 'remainingTotal', label: 'إجمالي المتبقي', money: true, danger: true }
      ],
      totalsRow: (t) => ['المجموع', t.invoicesCount, t.purchasesTotal, '', t.qtyTotal, t.paidTotal, t.remainingTotal]
    };
  }
  if (kind === 'review') {
    return {
      title: 'فواتير قديمة بحاجة مراجعة',
      columns: [
        { key: 'supplierName', label: 'المورد' },
        { key: 'invoiceNumber', label: 'رقم الفاتورة', mono: true },
        { key: 'date', label: 'التاريخ', date: true },
        { key: 'total', label: 'الإجمالي', money: true },
        { key: 'paid', label: 'المدفوع', money: true },
        { key: 'remaining', label: 'المتبقي', money: true },
        { key: 'paymentMethod', label: 'طريقة الدفع' },
        { key: 'reviewReason', label: 'سبب المراجعة' }
      ],
      totalsRow: null
    };
  }
  return null;
}

function Cell({ col, row }) {
  const v = row[col.key];
  if (col.money) {
    return <span className={col.danger && Number(v) > 0 ? 'font-bold' : ''} style={col.danger && Number(v) > 0 ? { color: '#B91C1C' } : undefined}>{formatIQD(v)}</span>;
  }
  if (col.date) return <span className="text-ink-3">{v ? formatDate(v) : '—'}</span>;
  if (col.dateTime) return <span className="text-ink-3">{v ? formatDateTime(v) : '—'}</span>;
  if (col.pill) {
    const variant = /مسدد بالكامل|مسددة بالكامل|مرحّل/.test(v) ? 'success'
      : /جزئ/.test(v) ? 'warning'
        : /دائن/.test(v) ? 'info'
          : /معكوس|ملغاة/.test(v) ? 'muted' : 'danger';
    return <Pill variant={variant}>{v || '—'}</Pill>;
  }
  if (col.mono) return <span className="font-mono">{v || '—'}</span>;
  if (typeof v === 'number') return formatNumber(v);
  return v || '—';
}

/** تبويب «تقارير» داخل شاشة حسابات الموردين — خمسة تقارير بفلاتر وتصدير. */
export function SupplierReports({ suppliers = [] }) {
  const [kind, setKind] = useState('balances');
  const [supplierId, setSupplierId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [onlyDue, setOnlyDue] = useState(false);
  const [includeReversed, setIncludeReversed] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const tableRef = useRef(null);

  const params = useMemo(() => {
    const p = {};
    if (supplierId) p.supplierId = supplierId;
    if (from) p.from = from;
    if (to) p.to = to;
    if (onlyDue) p.onlyDue = true;
    if (includeReversed) p.includeReversed = true;
    return p;
  }, [supplierId, from, to, onlyDue, includeReversed]);

  const query = useSupplierReport(kind, params);
  const data = query.data;
  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const def = defsFor(kind);

  const exportRows = useMemo(() => {
    if (kind === 'aging') {
      return rows.map((r) => ({
        supplierName: r.supplierName,
        notDue: r.notDue, d1_30: r.d1_30, d31_60: r.d31_60, d61_90: r.d61_90, d90plus: r.d90plus,
        total: r.total
      }));
    }
    if (!def) return [];
    return rows.map((r) => {
      const o = {};
      for (const c of def.columns) {
        const v = r[c.key];
        o[c.key] = c.date || c.dateTime ? (v ? formatDate(v) : '') : v;
      }
      return o;
    });
  }, [rows, kind, def]);

  const exportColumns = kind === 'aging'
    ? [
      { key: 'supplierName', label: 'المورد' },
      { key: 'notDue', label: 'غير مستحقة بعد' },
      { key: 'd1_30', label: '1 – 30 يوماً' },
      { key: 'd31_60', label: '31 – 60 يوماً' },
      { key: 'd61_90', label: '61 – 90 يوماً' },
      { key: 'd90plus', label: 'أكثر من 90 يوماً' },
      { key: 'total', label: 'الإجمالي' }
    ]
    : (def ? def.columns.map((c) => ({ key: c.key, label: c.label })) : []);

  const title = kind === 'aging' ? 'تقرير أعمار ديون الموردين' : (def ? def.title : '');

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap no-print">
        <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] flex-wrap">
          {REPORTS.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => { setKind(r.key); setExpanded(null); }}
              className={clsx(
                'px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold',
                kind === r.key ? 'bg-orange text-white' : 'text-ink-2 hover:bg-white'
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap no-print">
        <select className="input" style={{ width: 200 }} value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
          <option value="">كل الموردين</option>
          {suppliers.map((s) => <option key={String(s._id)} value={String(s._id)}>{s.name}</option>)}
        </select>
        {kind !== 'aging' && kind !== 'balances' && kind !== 'review' ? (
          <>
            <label className="text-[12px] text-ink-3">من</label>
            <input type="date" className="input" style={{ width: 150 }} value={from} onChange={(e) => setFrom(e.target.value)} />
            <label className="text-[12px] text-ink-3">إلى</label>
            <input type="date" className="input" style={{ width: 150 }} value={to} onChange={(e) => setTo(e.target.value)} />
          </>
        ) : null}
        {kind === 'balances' ? (
          <label className="flex items-center gap-1.5 text-[12.5px] text-ink-2">
            <input type="checkbox" checked={onlyDue} onChange={(e) => setOnlyDue(e.target.checked)} />
            الموردون الذين عليهم مستحقات فقط
          </label>
        ) : null}
        {kind === 'payments' ? (
          <label className="flex items-center gap-1.5 text-[12.5px] text-ink-2">
            <input type="checkbox" checked={includeReversed} onChange={(e) => setIncludeReversed(e.target.checked)} />
            إظهار السندات المعكوسة
          </label>
        ) : null}
        <div className="ms-auto flex items-center gap-2">
          <ExportButton
            rows={exportRows}
            columns={exportColumns}
            filename={title.replace(/\s+/g, '-')}
            title={title}
            tableRef={tableRef}
          />
          <button type="button" className="btn" onClick={() => window.print()}>
            <Icon name="print" size={13} /> طباعة
          </button>
          <button type="button" className="btn" onClick={() => query.refetch()}>
            <Icon name="clock" size={13} /> تحديث
          </button>
        </div>
      </div>

      <div className="print-root">
        {/* شعار المركز من الإعدادات — أعلى الورقة ووسطها */}
        <PrintLogo />
        <div className="print-section text-center mb-2">
          <h2 className="text-[17px] font-extrabold m-0">{title}</h2>
          <div className="text-[12px] text-ink-3 mt-0.5">
            {from || to ? `المدة: من ${from || 'البداية'} إلى ${to || 'اليوم'}` : 'كل الفترات'}
            {supplierId ? ` — ${(suppliers.find((s) => String(s._id) === supplierId) || {}).name || ''}` : ''}
          </div>
        </div>

        <div className="overflow-x-auto" ref={tableRef}>
          {query.isLoading ? (
            <div className="text-center py-10 text-ink-3">جاري التحميل...</div>
          ) : kind === 'aging' ? (
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-ink-3 border-b border-line text-right">
                  <th className="p-2">المورد</th>
                  {(data?.buckets || []).map((b) => <th key={b.key} className="p-2">{b.label}</th>)}
                  <th className="p-2">الإجمالي</th>
                </tr>
              </thead>
              <tbody>
                {!rows.length ? (
                  <tr><td colSpan={7} className="text-center py-10 text-ink-3">لا ديون على الموردين</td></tr>
                ) : rows.map((r) => (
                  <tr key={String(r.supplierId)} className="border-b border-line">
                    <td className="p-2 font-bold">{r.supplierName}</td>
                    {(data?.buckets || []).map((b) => (
                      <td key={b.key} className="p-2" style={b.key === 'd90plus' && r[b.key] > 0 ? { color: '#B91C1C', fontWeight: 700 } : undefined}>
                        {r[b.key] ? formatIQD(r[b.key]) : '—'}
                      </td>
                    ))}
                    <td className="p-2 font-extrabold">{formatIQD(r.total)}</td>
                  </tr>
                ))}
                {rows.length ? (
                  <tr style={{ background: '#FAF6EE' }}>
                    <td className="p-2 font-bold">المجموع</td>
                    {(data?.buckets || []).map((b) => (
                      <td key={b.key} className="p-2 font-bold">{formatIQD(totals[b.key] || 0)}</td>
                    ))}
                    <td className="p-2 font-extrabold">{formatIQD(totals.total || 0)}</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          ) : kind === 'purchases' ? (
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-ink-3 border-b border-line text-right">
                  {def.columns.map((c) => <th key={c.key} className="p-2">{c.label}</th>)}
                  <th className="p-2 no-print"></th>
                </tr>
              </thead>
              <tbody>
                {!rows.length ? (
                  <tr><td colSpan={8} className="text-center py-10 text-ink-3">لا مشتريات في هذه المدة</td></tr>
                ) : rows.map((r) => (
                  <Fragment key={String(r.supplierId)}>
                    <tr className="border-b border-line">
                      {def.columns.map((c) => <td key={c.key} className="p-2"><Cell col={c} row={r} /></td>)}
                      <td className="p-2 no-print">
                        <button
                          type="button"
                          className="btn"
                          style={{ padding: '4px 8px' }}
                          onClick={() => setExpanded(expanded === String(r.supplierId) ? null : String(r.supplierId))}
                        >
                          <Icon name={expanded === String(r.supplierId) ? 'chevronD' : 'chevronL'} size={13} /> التفاصيل
                        </button>
                      </td>
                    </tr>
                    {expanded === String(r.supplierId) ? (
                      <tr className="border-b border-line">
                        <td colSpan={8} className="p-2" style={{ background: '#FAFAFA' }}>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <div className="text-[12px] font-bold mb-1">المنتجات والكميات</div>
                              <table className="w-full text-[11.5px]">
                                <thead>
                                  <tr className="text-ink-3 border-b border-line text-right">
                                    <th className="p-1">المنتج</th><th className="p-1">الكمية</th><th className="p-1">الإجمالي</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.products.map((p) => (
                                    <tr key={String(p.productId)} className="border-b border-line">
                                      <td className="p-1">{p.name}</td>
                                      <td className="p-1">{formatNumber(p.qty)}</td>
                                      <td className="p-1">{formatIQD(p.total)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div>
                              <div className="text-[12px] font-bold mb-1">تفاصيل الفواتير</div>
                              <table className="w-full text-[11.5px]">
                                <thead>
                                  <tr className="text-ink-3 border-b border-line text-right">
                                    <th className="p-1">الفاتورة</th><th className="p-1">التاريخ</th>
                                    <th className="p-1">الإجمالي</th><th className="p-1">المتبقي</th><th className="p-1">الحالة</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.invoices.map((i) => (
                                    <tr key={String(i.invoiceId)} className="border-b border-line">
                                      <td className="p-1 font-mono">{i.invoiceNumber}</td>
                                      <td className="p-1 text-ink-3">{formatDate(i.date)}</td>
                                      <td className="p-1">{formatIQD(i.total)}</td>
                                      <td className="p-1">{formatIQD(i.remaining)}</td>
                                      <td className="p-1">{i.paymentStatusLabel}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
                {rows.length && def.totalsRow ? (
                  <tr style={{ background: '#FAF6EE' }}>
                    {def.totalsRow(totals).map((v, i) => (
                      <td key={i} className="p-2 font-bold">
                        {typeof v === 'number' ? (def.columns[i] && def.columns[i].money ? formatIQD(v) : formatNumber(v)) : v}
                      </td>
                    ))}
                    <td className="p-2 no-print"></td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          ) : def ? (
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-ink-3 border-b border-line text-right">
                  {def.columns.map((c) => <th key={c.key} className="p-2">{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {!rows.length ? (
                  <tr>
                    <td colSpan={def.columns.length} className="text-center py-10 text-ink-3">
                      {kind === 'review' ? 'لا توجد فواتير بحاجة مراجعة' : 'لا بيانات في هذه المدة'}
                    </td>
                  </tr>
                ) : rows.map((r, i) => (
                  <tr key={String(r.invoiceId || r.paymentId || r.supplierId || i)} className="border-b border-line">
                    {def.columns.map((c) => <td key={c.key} className="p-2"><Cell col={c} row={r} /></td>)}
                  </tr>
                ))}
                {rows.length && def.totalsRow ? (
                  <tr style={{ background: '#FAF6EE' }}>
                    {def.totalsRow(totals).map((v, i) => (
                      <td key={i} className="p-2 font-bold">
                        {typeof v === 'number' ? (def.columns[i] && def.columns[i].money ? formatIQD(v) : formatNumber(v)) : v}
                      </td>
                    ))}
                  </tr>
                ) : null}
              </tbody>
            </table>
          ) : null}
        </div>
      </div>
    </div>
  );
}
